import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaChevronDown } from 'react-icons/fa';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// Font families (add to tailwind.config.js and index.css as described)
// font-cairo for Arabic, font-montserrat for English





interface HeroSectionProps {
  language?: 'ar' | 'en';
  headline?: {
    ar: string;
    en: string;
  };
  subheadline?: {
    ar: string;
    en: string;
  };
  images?: string[];
}

const HeroSection: React.FC<HeroSectionProps> = ({ 
  language = 'en',
  headline = {
    ar: 'شركة مكة البركة للمقاولات العامة',
    en: 'Mecca El Baraka For Construction',
  },
  subheadline = {
    ar: 'نبني المستقبل بالنزاهة والخبرة. حلول متكاملة في المقاولات والمحاجر بمصر والشرق الأوسط.',
    en: 'Building the future with integrity and expertise. Complete solutions in construction, contracting, and quarrying in Egypt and the Middle East.',
  },
  images = [
    '/images/image1.jpg',
    '/images/image2.jpg',
    '/images/image3.jpg',
    '/images/image4.jpg',
  ]
}) => {
  const isRTL = language === 'ar';

  // Animation state
  const [showHeroText, setShowHeroText] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    let fadeOutTimeout: NodeJS.Timeout;
    let hideTimeout: NodeJS.Timeout;
    if (showHeroText) {
      fadeOutTimeout = setTimeout(() => setFadeOut(true), 8000); // زيادة الوقت قبل الاختفاء
      hideTimeout = setTimeout(() => setShowHeroText(false), 10000); // زيادة الوقت الكلي
    }
    return () => {
      clearTimeout(fadeOutTimeout);
      clearTimeout(hideTimeout);
    };
  }, [showHeroText]);



  const scrollToNext = () => {
    const nextSection = document.getElementById('about-preview');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const restartAnimation = () => {
    setShowHeroText(false);
    setFadeOut(false);
    setTimeout(() => {
      setShowHeroText(true);
    }, 100);
  };

  return (
    <section
      className="relative h-screen flex items-center justify-center overflow-hidden font-cairo"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Swiper Background */}
      <div className="absolute inset-0 z-0">
        <Swiper
          dir={isRTL ? 'rtl' : 'ltr'}
          key={language}
          modules={[Autoplay, Navigation, Pagination]}
          autoplay={{ delay: 3500, disableOnInteraction: false }}
          loop={true}
          navigation={true}
          pagination={{ clickable: true }}
          className="w-full h-full"
        >
          {images.map((src, idx) => (
            <SwiperSlide key={idx}>
              <motion.img
                src={src}
                alt={`Construction Slide ${idx + 1}`}
                className="w-full h-full object-cover"
                draggable="false"
                data-editable="true"
                data-image-key={`images.${idx}`}
                initial={{ scale: 1.1, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  duration: 1.5,
                  ease: "easeOut"
                }}
                whileHover={{ scale: 1.05 }}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <motion.div 
          className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-primary-900/60"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5, delay: 0.5 }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center text-white px-4">
        <motion.h1 
          className="text-4xl md:text-6xl font-bold mb-6"
          initial={{ opacity: 0, x: isRTL ? 200 : -200 }}
          animate={{ 
            opacity: fadeOut ? 0 : 1, 
            x: fadeOut ? (isRTL ? -200 : 200) : 0 
          }}
          transition={{ 
            duration: fadeOut ? 2 : 3, // دخول بطيء، خروج بطيء
            ease: "easeInOut"
          }}
          data-editable="true"
          data-text-key="headline.ar"
        >
          {(isRTL ? headline.ar : headline.en).split(' ').map((word, index) => (
            <motion.span
              key={index}
              initial={{ opacity: 0, x: isRTL ? 100 : -100 }}
              animate={{ 
                opacity: fadeOut ? 0 : 1, 
                x: fadeOut ? (isRTL ? -100 : 100) : 0 
              }}
              transition={{
                duration: fadeOut ? 1.5 : 2,
                delay: fadeOut ? 0 : index * 0.1,
                ease: "easeInOut"
              }}
              className="inline-block mr-2"
            >
              {word}
            </motion.span>
          ))}
        </motion.h1>
        <motion.p 
          className="text-lg md:text-xl mb-8 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, x: isRTL ? 200 : -200 }}
          animate={{ 
            opacity: fadeOut ? 0 : 1, 
            x: fadeOut ? (isRTL ? -200 : 200) : 0 
          }}
          transition={{ 
            duration: fadeOut ? 2 : 3,
            delay: fadeOut ? 0.5 : 0.5, // تأخير قليل للفقرة الفرعية
            ease: "easeInOut"
          }}
          data-editable="true"
          data-text-key="subheadline.ar"
        >
          {(isRTL ? subheadline.ar : subheadline.en).split(' ').map((word, index) => (
            <motion.span
              key={index}
              initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
              animate={{ 
                opacity: fadeOut ? 0 : 1, 
                x: fadeOut ? (isRTL ? -50 : 50) : 0 
              }}
              transition={{
                duration: fadeOut ? 1.5 : 2,
                delay: fadeOut ? 0.5 : 0.5 + (index * 0.05),
                ease: "easeInOut"
              }}
              className="inline-block mr-1"
            >
              {word}
            </motion.span>
          ))}
        </motion.p>
      </div>

      {/* Scroll Indicator */}
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        onClick={scrollToNext}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 text-white hover:text-gold-400 transition-colors"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <FaChevronDown className="w-6 h-6" />
        </motion.div>
      </motion.button>

      {/* Restart Animation Button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.8 }}
        onClick={restartAnimation}
        className="absolute top-8 right-8 z-10 text-white hover:text-gold-400 transition-colors bg-black/20 backdrop-blur-sm rounded-full p-2"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        title="إعادة تشغيل الانيميشن"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </motion.button>
    </section>
  );
};

export default HeroSection; 